import { useEffect, useRef } from 'react'

import type { Track } from 'types/track'

import {
  type AvailableRightsTypes,
  sendOpenRightsTabEvent,
} from 'features/rights-data/utils/rights-mixpanel'

interface UseOpenRightsTabEventArgs {
  activeTab: 'publishing' | 'recording'
  isReady: boolean
  availableRightsTypes: AvailableRightsTypes
  track?: Track | null
  trackId: number | null
  /** Defined only in bulk mode */
  bulkTrackCount?: number
  /** Saved splits per rights type, as loaded from the server */
  splitCounts: { publishing: number; recording: number }
}

/**
 * Sends `Open rights tab` once per track and sub-tab view, after the rights
 * have loaded. Call `markSwitch` when the user changes sub-tab.
 *
 * Nothing is sent if the rights fail to load: the ticket defines the event as
 * firing once loading has finished, and the error view has no split data to
 * report.
 */
export function useOpenRightsTabEvent({
  activeTab,
  isReady,
  availableRightsTypes,
  track,
  trackId,
  bulkTrackCount,
  splitCounts,
}: UseOpenRightsTabEventArgs) {
  const sentKey = useRef<string | null>(null)
  const trigger = useRef<'Default' | 'Switch'>('Default')
  // Read at send time so a later change doesn't re-send the event
  const latest = useRef({ availableRightsTypes, track, splitCounts })
  latest.current = { availableRightsTypes, track, splitCounts }

  const viewKey = `${trackId ?? 'bulk'}:${activeTab}`

  useEffect(() => {
    if (!isReady || sentKey.current === viewKey) return
    sentKey.current = viewKey

    const current = latest.current
    const splitCount = current.splitCounts[activeTab]
    sendOpenRightsTabEvent({
      rightsType: activeTab === 'publishing' ? 'Publishing' : 'Recording',
      trigger: trigger.current,
      availableRightsTypes: current.availableRightsTypes,
      track: current.track,
      trackId,
      bulkTrackCount,
      hasData: splitCount > 0,
      splitCount,
    })
    trigger.current = 'Default'
  }, [isReady, viewKey, activeTab, trackId, bulkTrackCount])

  return {
    markSwitch: () => {
      // A switch before loading finishes is still the opening view: Default
      if (isReady) trigger.current = 'Switch'
    },
  }
}
